import { endOfMonth, format, getYear } from "date-fns";
import { Badge, Tooltip } from "@jhool-io/fe-components";
import { FormatOptionLabelMeta } from "react-select";
import {
    BillPaymentStatus,
    BillStatus,
    BillStatusLabels,
} from "../types/billing.types";
import { formatDate, removeEnumUnderscore } from "../../../utils/helpers";
import { PaymentChannel } from "../../payments/types/payments.types";
import InfoIcon from "../../../icons/Info";

type Options = {
    label: React.ReactNode;
    value: string | undefined;
};

interface TooltipWrapperProps {
    value: string;
    description: string;
}

const currentYear = getYear(new Date());

const billStatusDescriptions: Record<BillStatus, string> = {
    [BillStatus.OPEN]: "",
    [BillStatus.CLOSED]: "",
    [BillStatus.PENDING_RESOLUTION]: "",
    [BillStatus.PENDING_PATIENT_RESOLUTION]: "The patient has not paid PR100",
    [BillStatus.PENDING_INSURANCE_RESOLUTION]:
        "The insurance company has not paid",
    [BillStatus.CARD_PAYMENT_DECLINED]: "",
    [BillStatus.CARD_PAYMENT_DECLINED_PR100]: "",
    [BillStatus.UNCOLLECTABLE]:
        "The client has a balance, but all collection efforts have been exhausted",
    [BillStatus.CREDIT_BALANCE]: "The client has a credit balance",
};

export const BillingSummaryCardsFilters = [
    "year_to_date",
    `${currentYear - 1}`,
    `${currentYear - 2}`,
    `${currentYear - 3}`,
] as const;

export const billingPaymentMonths = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
];

// Get correct from_date and to_date from BillingCardsFilters
export const getTimeRangeFromFilterValue = (filter: string) => {
    switch (filter) {
        case BillingSummaryCardsFilters[0]:
            return {
                fromDate: `${currentYear}-01-01`,
                toDate: format(endOfMonth(new Date()), `yyyy-MM-dd`),
            };

        case BillingSummaryCardsFilters[1]:
            return {
                fromDate: `${BillingSummaryCardsFilters[1]}-01-01`,
                toDate: `${BillingSummaryCardsFilters[1]}-12-31`,
            };

        case BillingSummaryCardsFilters[2]:
            return {
                fromDate: `${BillingSummaryCardsFilters[2]}-01-01`,
                toDate: `${BillingSummaryCardsFilters[2]}-12-31`,
            };

        case BillingSummaryCardsFilters[3]:
            return {
                fromDate: `${BillingSummaryCardsFilters[3]}-01-01`,
                toDate: `${BillingSummaryCardsFilters[3]}-12-31`,
            };

        default:
            return {
                fromDate: "",
                toDate: "",
            };
    }
};

export const getBillStatusTag = (status: BillStatus, text?: string) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case BillStatus.OPEN:
        case BillStatus.CLOSED:
            textColor = "var(--color-neutral-text)";
            bgColor = "var(--color-neutral-bg)";
            break;
        case BillStatus.PENDING_RESOLUTION:
        case BillStatus.PENDING_PATIENT_RESOLUTION:
        case BillStatus.PENDING_INSURANCE_RESOLUTION:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        case BillStatus.UNCOLLECTABLE:
        case BillStatus.CREDIT_BALANCE:
            textColor = "var(--color-info-text)";
            bgColor = "var(--color-info-bg)";
            break;
        case BillStatus.CARD_PAYMENT_DECLINED:
        case BillStatus.CARD_PAYMENT_DECLINED_PR100:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;

        default:
            textColor = "var(--color-neutral-text)";
            bgColor = "var(--color-neutral-bg)";
    }

    return (
        <Badge
            type="custom"
            textColor={textColor}
            bgColor={bgColor}
            content={`${text ? `${text} ` : ``}${removeEnumUnderscore(
                BillStatusLabels[status]
            )}`}
        />
    );
};

export const getBillStatusFilterLabel = (status: BillStatus) => {
    const description = billStatusDescriptions[status];
    const label = removeEnumUnderscore(BillStatusLabels[status]);

    return description ? (
        <Tooltip content={description} classNames="!z-[100]">
            <span className="flex items-center gap-x-2">
                {label} <InfoIcon className="size-3" />
            </span>
        </Tooltip>
    ) : (
        <span>{label}</span>
    );
};

export const getBillPaymentStatusTag = (status: BillPaymentStatus) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case BillPaymentStatus.COMPLETED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case BillPaymentStatus.FAILED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;
        case BillPaymentStatus.PENDING:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        case BillPaymentStatus.REFUNDED:
            textColor = "var(--color-info-text)";
            bgColor = "var(--color-info-bg)";
            break;
        case BillPaymentStatus.VOIDED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;
        default:
            textColor = "var(--color-neutral-text)";
            bgColor = "var(--color-neutral-bg)";
    }

    return (
        <Badge
            textColor={textColor}
            bgColor={bgColor}
            content={removeEnumUnderscore(status)}
            type="custom"
        />
    );
};

export const getBillingSummaryNavigateUrl = () => {
    // Get billing summary filters from localstorage
    const billingSummaryFiltersFromLS = localStorage.getItem(
        "billingsummary_filters"
    );

    if (billingSummaryFiltersFromLS) {
        return `/billing-summary?${billingSummaryFiltersFromLS}`;
    }
    return `/billing-summary`;
};

export const paymentDateToDisplay = (
    paymentDate: string,
    paymentChannel: string
) => {
    if (paymentDate) {
        if (paymentChannel === PaymentChannel.AUTHORIZE_NET) {
            return formatDate(paymentDate);
        }
        return formatDate(paymentDate.replace("+00:00", ""), true);
    }
    return "--";
};

function TooltipWrapper({ value, description }: TooltipWrapperProps) {
    return (
        <Tooltip content={description}>
            <span>{value}</span>
        </Tooltip>
    );
}

export const formatRemarkOptionLabel = (
    data: unknown,
    formatOptionLabelMeta: FormatOptionLabelMeta<unknown>,
    getRemarkCodeDescription: (code: string) => string,
    codeInView: string
) => {
    return formatOptionLabelMeta.context === "menu" ? (
        (data as Options).label
    ) : (
        <TooltipWrapper
            value={
                codeInView === "writeoff_code"
                    ? `Write Off: ${(data as Options).value as string}`
                    : ((data as Options).value as string)
            }
            description={getRemarkCodeDescription(
                (data as Options).value as string
            )}
        />
    );
};

export const getBillStatusLabel = (status: BillStatus) =>
    BillStatusLabels[status] ?? status;
